import { datadogLogs } from "@datadog/browser-logs";

import { clientToken } from "./token";

export type LogLevel = "debug" | "info" | "warn" | "error";

const isProduction = process.env["NODE_ENV"] === "production";
const isDesktopAppMode = process.env["MODE"] === "desktop";
const isStaging = process.env["APP_ENV"] === "staging";
const isLocal = process.env["APP_ENV"] === "local" || process.env["APP_ENV"] === "testing";

// this sets up browser level logging.
export const initLogger = (service: string) => {
	if (isLocal) {
		// We don't want to log to Datadog if we're developing locally
		// because it's too noisy.
		return;
	}

	if (typeof window === "undefined") {
		return;
	}

	if (service === "") {
		service = "unknown";
	}

	console.debug(`setting up logging with service ${service} app ${process.env["APP_ENV"]}`);

	datadogLogs.init({
		clientToken: clientToken(),
		site: "datadoghq.com",
		service,
		env: process.env["APP_ENV"],
		silentMultipleInit: true,
		// Forward console errors.
		forwardErrorsToLogs: true,
		allowFallbackToLocalStorage: true,
		// We can enable this if we want to send all console logs.
		forwardConsoleLogs: "all",
	});
};

export const log = (
	message: string,
	content?: Record<string, unknown>,
	level: LogLevel = "info"
): void => {
	async function _() {
		if (typeof window === "undefined") {
			return;
		}

		if (typeof document === "undefined") {
			return;
		}

		// Extract the first error found in the content record.
		let error: Error | undefined = undefined;
		for (const v of Object.values(content ?? {})) {
			if (v instanceof Error) {
				error = v;
				break;
			}
		}
		switch (level) {
			case "debug": {
				!isProduction && console.debug(`${level.toUpperCase()} ${message}`, content);
				break;
			}
			case "info": {
				(!isProduction || (isDesktopAppMode && isStaging)) &&
					console.info(`${level.toUpperCase()} ${message}`, content);
				!isLocal && datadogLogs.logger.info(message, content, error);
				break;
			}
			case "warn": {
				(!isProduction || (isDesktopAppMode && isStaging)) &&
					console.warn(`${level.toUpperCase()} ${message}`, content);

				!isLocal && datadogLogs.logger.warn(message, content, error);
				break;
			}
			case "error": {
				(!isProduction || (isDesktopAppMode && isStaging)) &&
					console.error(`${level.toUpperCase()} ${message}`, content);
				!isLocal && datadogLogs.logger.error(message, content, error);
				break;
			}
			default: {
				!isProduction && console.error("Unknown log level", { level, ...content });
				!isLocal && datadogLogs.logger.error("Unknown log level", { level, ...content });
				!isLocal && datadogLogs.logger.info(message, content, error);
				break;
			}
		}
	}

	void _();
};

export type UserInfo = {
	// this will be the auth0 ID if the user is logged in.
	id: string;
	email: string | undefined;
	isMoment: boolean;
	loggedIn: boolean;
	name?: string;
};

export const setUser = (userInfo: UserInfo) => {
	datadogLogs.setUser({
		...userInfo,
	});
};

export const setUserProperty = (key: string, value: unknown) => {
	datadogLogs.setUserProperty(key, value);
};

export const setGlobalContext = (key: string, value: unknown) => {
	datadogLogs.setGlobalContextProperty(key, value);
};

export type LogFunc = (message: string, content?: Record<string, unknown>) => void;

export type BaseLogFunc = (
	level: LogLevel,
	message: string,
	content?: Record<string, unknown>
) => void;

/**
 * Javascript Class for use in non-react contexts
 *
 *
 * @param moduleName - string, name of the module to log

* @returns Logger - class instance with debug, info, warn, and error methods
 */
export class Logger {
	private moduleNamePrefix: string;

	constructor(moduleName: string) {
		this.moduleNamePrefix = `[${moduleName}] `;
	}

	private baseLogger: BaseLogFunc = (level, message, content) =>
		log(`${this.moduleNamePrefix}${message}`, { level, ...content }, level);

	public debug: LogFunc = (message, content) => this.baseLogger("debug", message, content);

	public info: LogFunc = (message, content) => this.baseLogger("info", message, content);

	public warn: LogFunc = (message, content) => this.baseLogger("warn", message, content);

	public error: LogFunc = (message, content) => this.baseLogger("error", message, content);
}

export class NoopLogger {
	constructor(_moduleName: string) {}
	public debug: LogFunc = () => {};

	public info: LogFunc = () => {};

	public warn: LogFunc = () => {};

	public error: LogFunc = () => {};
}
